import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, FileText, Trash2, AlertCircle, BookOpen, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SourceDocument {
  id: string;
  filename: string;
  file_path: string;
  description: string | null;
  age_min_months: number | null;
  age_max_months: number | null;
  topics: string[] | null;
  upload_status: string;
  chunk_count: number | null;
  created_at: string;
}

export function SleepContentManagement() {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<SourceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Form state
  const [description, setDescription] = useState('');
  const [ageMin, setAgeMin] = useState('0');
  const [ageMax, setAgeMax] = useState('24');
  const [topics, setTopics] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('sleep_source_documents' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments((data as any) || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: "Error",
        description: "Failed to load documents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid file type",
          description: "Please select a PDF file",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleAnalyzeWithAI = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a PDF file first",
        variant: "destructive",
      });
      return;
    }

    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const { data, error } = await supabase.functions.invoke('analyze-pdf-content', {
        body: formData,
      });

      if (error) throw error;

      if (data.success && data.analysis) {
        const { description: aiDescription, age_min_months, age_max_months, topics: aiTopics } = data.analysis;
        
        setDescription(aiDescription || '');
        setAgeMin(age_min_months?.toString() || '0');
        setAgeMax(age_max_months?.toString() || '24');
        setTopics(Array.isArray(aiTopics) ? aiTopics.join(', ') : '');

        toast({
          title: "AI Analysis Complete",
          description: "Form fields have been auto-filled based on PDF content",
        });
      } else {
        throw new Error('Failed to analyze PDF');
      }
    } catch (error) {
      console.error('Error analyzing PDF:', error);
      toast({
        title: "Analysis Failed",
        description: "Could not analyze PDF. Please fill the form manually.",
        variant: "destructive",
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a PDF file to upload",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('description', description);
      formData.append('age_min_months', ageMin);
      formData.append('age_max_months', ageMax);
      formData.append('topics', JSON.stringify(topics.split(',').map(t => t.trim()).filter(Boolean)));

      const { data, error } = await supabase.functions.invoke('upload-sleep-content', {
        body: formData,
      });

      if (error) throw error;

      toast({
        title: "Upload successful",
        description: `Document uploaded and processing started. ${data.chunk_count} chunks created.`,
      });

      // Reset form
      setSelectedFile(null);
      setDescription('');
      setAgeMin('0');
      setAgeMax('24');
      setTopics('');
      
      // Refresh list
      fetchDocuments();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload document",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId: string, filePath: string) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('sleep-source-documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete chunks (cascade will handle this, but being explicit)
      const { error: chunksError } = await supabase
        .from('sleep_content_chunks' as any)
        .delete()
        .eq('document_id', documentId);

      if (chunksError) throw chunksError;

      // Delete document record
      const { error: docError } = await supabase
        .from('sleep_source_documents' as any)
        .delete()
        .eq('id', documentId);

      if (docError) throw docError;

      toast({
        title: "Document deleted",
        description: "Document and all associated chunks removed",
      });

      fetchDocuments();
    } catch (error: any) {
      console.error('Delete error:', error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete document",
        variant: "destructive",
      });
    } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertCircle className="h-5 w-5 text-blue-500" />
            Sleep Content Knowledge Base
          </CardTitle>
          <CardDescription>
            Upload PDFs containing sleep training methods, research, and guidance. This content is used to generate personalized sleep plans with RAG (Retrieval Augmented Generation).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• PDFs are automatically chunked and indexed for semantic search</p>
          <p>• Specify age ranges and topics to help target recommendations</p>
          <p>• Minimum 3-5 documents recommended for quality sleep plans</p>
          <p>• If insufficient content, users will be directed to the <BookOpen className="inline h-4 w-4" /> Books Library</p>
        </CardContent>
      </Card>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload New Document
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="file">PDF Document</Label>
            <Input
              id="file"
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              disabled={uploading || analyzing}
            />
            {selectedFile && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAnalyzeWithAI}
                  disabled={analyzing || uploading}
                  className="w-full"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {analyzing ? 'Analyzing with AI...' : 'Auto-fill with AI'}
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Brief description of the content..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ageMin">Min Age (months)</Label>
              <Input
                id="ageMin"
                type="number"
                min="0"
                max="60"
                value={ageMin}
                onChange={(e) => setAgeMin(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ageMax">Max Age (months)</Label>
              <Input
                id="ageMax"
                type="number"
                min="0"
                max="60"
                value={ageMax}
                onChange={(e) => setAgeMax(e.target.value)}
                disabled={uploading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="topics">Topics (comma-separated)</Label>
            <Input
              id="topics"
              placeholder="e.g. naps, night wakings, sleep training"
              value={topics}
              onChange={(e) => setTopics(e.target.value)}
              disabled={uploading}
            />
          </div>

          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="w-full h-12"
          >
            {uploading ? "Uploading & Processing..." : "Upload Document"}
          </Button>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Uploaded Documents ({documents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Loading documents...</p>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 space-y-2">
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-muted-foreground">No documents uploaded yet</p>
              <p className="text-sm text-muted-foreground">Upload at least 3-5 documents for quality sleep plans</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <p className="font-medium truncate">{doc.filename}</p>
                      <Badge variant={doc.upload_status === 'completed' ? 'default' : 'secondary'}>
                        {doc.upload_status}
                      </Badge>
                    </div>
                    {doc.description && (
                      <p className="text-sm text-muted-foreground mb-2">{doc.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {doc.age_min_months !== null && doc.age_max_months !== null && (
                        <span>Age: {doc.age_min_months}-{doc.age_max_months} months</span>
                      )}
                      {doc.topics && doc.topics.length > 0 && (
                        <span>Topics: {doc.topics.join(', ')}</span>
                      )}
                      {doc.chunk_count && (
                        <span>{doc.chunk_count} chunks</span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteConfirm(doc.id)}
                    className="flex-shrink-0 ml-4 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the document and all associated chunks from the knowledge base.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const doc = documents.find(d => d.id === deleteConfirm);
                if (doc) handleDelete(doc.id, doc.file_path);
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
